import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'

import Flex from '@react-css/flex'
import Modal from 'react-modal'
import { useHistory } from 'react-router-dom'

import { formaLoginUrl } from '../../../api/autodeskFormaAuth'
import {
  HexagonSyncPreviewImageItem,
  HexagonSyncPreviewResponse,
  HexagonSyncProgressSummary,
  HexagonSyncRunSummary,
  getHexagonSyncStatus,
  previewHexagonSync,
  startHexagonSyncJob,
} from '../../../api/hexagon'
import { p6LoginUrl } from '../../../api/oracleP6Auth'
import { unifierLoginUrl } from '../../../api/oracleUnifierAuth'
import {
  getAccessToken,
  procoreLoginUrl,
  verifyProcoreToken,
} from '../../../api/procore'
import formaLogo from '../../../assets/images/logos/autodesk_forma_logo.png'
import droneDeployLogo from '../../../assets/images/logos/drone_deploy_logo.png'
import hexagonLogo from '../../../assets/images/logos/hexagon_multivista_logo.png'
import oracleP6Logo from '../../../assets/images/logos/oracle_p6_logo.png'
import oracleUnifierLogo from '../../../assets/images/logos/oracle_unifier_logo.png'
import procoreLogo from '../../../assets/images/logos/procore_logo.png'
import { useNotifications } from '../../../contexts/notificationProvider'
import { useUserContext } from '../../../contexts/userContext'
import { PATH_STRINGS } from '../../../hooks/useGeneratedPaths'
import { useQueryParams } from '../../../hooks/useQueryParams'
import { resolvePublicAppBaseUrl } from '../../../utils/resolvePublicAppBaseUrl'
import { parseXerSchedule } from '../../../utils/xer'
import { LoadingIndicator } from '../../common/LoadingIndicator'
import { AddProjectButton } from '../buildings_page/components/AddProjectButton'
import {
  CardBase,
  CardDesc,
  CardDescItem,
  CardImage,
  CardTitle,
  CardTitleInner,
} from '../buildings_page/components/Card'

import { IntegrationAuthCard } from './IntegrationAuthCard'
import { startScheduleImportSession } from './schedule_import/scheduleImportSession'

function hexagonPreviewDidgePointUrl(
  projectPublicId: string,
  floorCode: string,
  subViewpointId: number,
): string | null {
  try {
    const base = resolvePublicAppBaseUrl()
    return `${base}/project/${encodeURIComponent(projectPublicId)}/floor/${encodeURIComponent(
      floorCode,
    )}/point/${subViewpointId}`
  } catch {
    return null
  }
}

export const Integrations = () => {
  const { state: userState } = useUserContext()
  const isSuperUser = userState.user_type === 'super'
  const queryParams = useQueryParams()
  const authorizationCode = queryParams.get('code')
  const history = useHistory()
  const { addNotification } = useNotifications()
  const scheduleFileInputRef = useRef<HTMLInputElement>(null)
  const [scheduleUploadBusy, setScheduleUploadBusy] = useState(false)

  const [linkedWithProcore, setLinkedWithProcore] = useState<boolean>(false)
  const [dataLoaded, setDataLoaded] = useState<boolean>(false)
  const [hexagonJobId, setHexagonJobId] = useState<string | null>(null)
  const [hexagonStatus, setHexagonStatus] = useState<
    'idle' | 'queued' | 'running' | 'success' | 'failed'
  >('idle')
  const [hexagonPreviewLoading, setHexagonPreviewLoading] =
    useState<boolean>(false)
  const [hexagonPreviewModalOpen, setHexagonPreviewModalOpen] =
    useState<boolean>(false)
  const [hexagonPreviewModalTitle, setHexagonPreviewModalTitle] =
    useState<string>('Hexagon Preview')
  const [hexagonPreviewModalMessage, setHexagonPreviewModalMessage] =
    useState<string>('')
  const [hexagonPreviewData, setHexagonPreviewData] =
    useState<HexagonSyncPreviewResponse | null>(null)
  const [hexagonStartDate, setHexagonStartDate] = useState<string>('')
  const [hexagonEndDate, setHexagonEndDate] = useState<string>('')
  const [hexagonPreviewPage, setHexagonPreviewPage] = useState<number>(1)
  const [hexagonPreviewPageLoading, setHexagonPreviewPageLoading] =
    useState<boolean>(false)
  const [hexagonSyncClosestVpMatch, setHexagonSyncClosestVpMatch] =
    useState<boolean>(false)
  const [hexagonSyncReportOpen, setHexagonSyncReportOpen] =
    useState<boolean>(false)
  const [hexagonSyncReportSummary, setHexagonSyncReportSummary] =
    useState<HexagonSyncRunSummary | null>(null)

  const num = (v: unknown, fallback = 0) => {
    const n = Number(v)
    return Number.isFinite(n) ? n : fallback
  }
  const previewNeedsRefresh = Boolean(
    hexagonPreviewData &&
      hexagonPreviewData.sync_closest_vp_match !== hexagonSyncClosestVpMatch,
  )

  const checkUserProcoreTokens = useCallback(() => {
    verifyProcoreToken()
      .then(() => {
        setLinkedWithProcore(true)
      })
      .catch(() => {
        setLinkedWithProcore(false)
      })
      .finally(() => {
        setDataLoaded(true)
      })
  }, [])

  const openScheduleFilePicker = () => {
    scheduleFileInputRef.current?.click()
  }

  const onScheduleFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) {
      return
    }

    const lowerName = file.name.toLowerCase()
    if (!lowerName.endsWith('.xer')) {
      addNotification(
        'Please upload a Primavera .xer schedule file.',
        'warning',
      )
      return
    }

    setScheduleUploadBusy(true)
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const text = String(reader.result ?? '')
        const schedule = parseXerSchedule(text)
        if (schedule.tasks.length === 0) {
          addNotification('No TASK rows found in that .xer file.', 'warning')
          return
        }
        startScheduleImportSession(file.name, schedule)
        history.push(PATH_STRINGS.userProfileScheduleImport)
      } catch (err) {
        addNotification('Failed to parse the .xer schedule file.', 'error')
      } finally {
        setScheduleUploadBusy(false)
      }
    }
    reader.onerror = () => {
      setScheduleUploadBusy(false)
      addNotification('Failed to read the selected file.', 'error')
    }
    reader.readAsText(file)
  }

  useEffect(() => {
    if (!linkedWithProcore) {
      setDataLoaded(false)

      if (authorizationCode) {
        getAccessToken(authorizationCode)
          .then(() => {
            setLinkedWithProcore(true)
          })
          .catch(() => {
            checkUserProcoreTokens()
          })
          .finally(() => {
            setDataLoaded(true)
          })
      } else {
        checkUserProcoreTokens()
      }
    }
  }, [authorizationCode, linkedWithProcore, checkUserProcoreTokens])

  useEffect(() => {
    if (!hexagonJobId || !['queued', 'running'].includes(hexagonStatus)) {
      return
    }
    const timer = window.setInterval(() => {
      getHexagonSyncStatus(hexagonJobId)
        .then(status => {
          if (status.status === 'success') {
            setHexagonStatus('success')
            const summary = (status.summary ?? {}) as HexagonSyncRunSummary
            setHexagonSyncReportSummary(summary)
            setHexagonSyncReportOpen(true)
            const uploadedCount = num(summary.uploaded_images)
            const directVp = num(summary.uploaded_via_direct_vp)
            const closestVp = num(summary.uploaded_via_closest_vp)
            const skippedAlready = num(summary.skipped_already_synced)
            const noopDates = num(summary.dates_skipped_no_pending_work)
            const dbByFloor = summary.db_viewpoint_grid_point_count_by_floor
            const dbHint =
              dbByFloor && typeof dbByFloor === 'object'
                ? ` · DB grid points: ${Object.entries(dbByFloor)
                    .map(([code, n]) => `${code}=${n}`)
                    .join(', ')}`
                : ''
            const idemHint =
              uploadedCount > 0 && (skippedAlready > 0 || noopDates > 0)
                ? ` · skipped already synced ${skippedAlready}${
                    noopDates > 0
                      ? ` · dates with no pending work ${noopDates}`
                      : ''
                  }`
                : ''
            let uploadHint: string
            if (uploadedCount === 0 && skippedAlready > 0) {
              uploadHint = `No new images uploaded (${skippedAlready} matching photo(s) were already synced${
                noopDates > 0 ? `; ${noopDates} date(s) had nothing to do` : ''
              })${dbHint}`
            } else {
              uploadHint = `Sync complete (${uploadedCount} new images · direct VP ${directVp} · closest VP ${closestVp}${dbHint}${idemHint})`
            }
            setHexagonPreviewModalTitle('Hexagon Sync Complete')
            setHexagonPreviewModalMessage(uploadHint)
            setHexagonPreviewModalOpen(true)
          } else if (status.status === 'failed') {
            setHexagonStatus('failed')
            const firstError = status.errors?.[0] ?? 'Sync failed'
            setHexagonPreviewModalTitle('Hexagon Sync Error')
            setHexagonPreviewModalMessage(firstError)
            setHexagonPreviewModalOpen(true)
          } else if (
            status.status === 'running' ||
            status.status === 'queued'
          ) {
            setHexagonStatus(status.status)
            const s = status.summary as HexagonSyncProgressSummary | undefined
            const progressLabel =
              status.status === 'queued'
                ? 'Queued — waiting for worker to start...'
                : s?.phase === 'uploading'
                  ? `Uploading... ${s?.uploaded_images ?? 0} image(s) uploaded so far${
                      s?.current_floor ? ` · floor ${s.current_floor}` : ''
                    }${s?.current_date ? ` · ${s.current_date}` : ''}`
                  : s?.phase === 'floor'
                    ? `Preparing floor ${s.current_floor ?? '—'}...`
                    : s?.phase === 'starting'
                      ? 'Connecting to Hexagon...'
                      : 'Working...'
            setHexagonPreviewModalTitle('Hexagon Sync In Progress')
            setHexagonPreviewModalMessage(progressLabel)
            setHexagonPreviewModalOpen(true)
          }
        })
        .catch(() => {
          setHexagonStatus('failed')
          setHexagonPreviewModalTitle('Hexagon Sync Error')
          setHexagonPreviewModalMessage('Unable to read sync status')
          setHexagonPreviewModalOpen(true)
        })
    }, 2000)

    return () => {
      window.clearInterval(timer)
    }
  }, [hexagonJobId, hexagonStatus])

  const startHexagonSync = () => {
    if (!hexagonStartDate || !hexagonEndDate) {
      setHexagonPreviewModalTitle('Hexagon Sync Error')
      setHexagonPreviewModalMessage(
        'Please select both start and end dates before syncing.',
      )
      setHexagonPreviewData(null)
      setHexagonPreviewModalOpen(true)
      return
    }
    setHexagonStatus('queued')
    setHexagonSyncReportSummary(null)
    setHexagonSyncReportOpen(false)
    startHexagonSyncJob({
      startDate: hexagonStartDate || undefined,
      endDate: hexagonEndDate || undefined,
      syncClosestVpMatch: hexagonSyncClosestVpMatch,
    })
      .then(payload => {
        setHexagonJobId(payload.job_id)
        setHexagonStatus(payload.status)
        setHexagonPreviewModalTitle('Hexagon Sync Queued')
        setHexagonPreviewModalMessage(`Queued sync job ${payload.job_id}.`)
        setHexagonPreviewModalOpen(true)
      })
      .catch(error => {
        const detail =
          error?.response?.data?.detail ??
          'Unable to start sync. Check Hexagon config in API env.'
        setHexagonStatus('failed')
        setHexagonPreviewModalTitle('Hexagon Sync Error')
        setHexagonPreviewModalMessage(detail)
        setHexagonPreviewModalOpen(true)
      })
  }

  const runHexagonPreview = () => {
    if (!hexagonStartDate || !hexagonEndDate) {
      setHexagonPreviewModalTitle('Hexagon Preview Error')
      setHexagonPreviewModalMessage(
        'Please select both start and end dates before previewing.',
      )
      setHexagonPreviewData(null)
      setHexagonPreviewModalOpen(true)
      return
    }
    setHexagonPreviewLoading(true)
    setHexagonPreviewPageLoading(true)
    setHexagonPreviewData(null)
    setHexagonPreviewModalTitle('Hexagon Preview')
    setHexagonPreviewModalMessage('Preparing sync preview…')
    setHexagonPreviewModalOpen(true)
    previewHexagonSync({
      maxPhotosPerDate: 6,
      previewPage: hexagonPreviewPage,
      imagesPerPage: 8,
      startDate: hexagonStartDate || undefined,
      endDate: hexagonEndDate || undefined,
      syncClosestVpMatch: hexagonSyncClosestVpMatch,
    })
      .then(preview => {
        setHexagonPreviewModalTitle('Hexagon Preview Results')
        setHexagonPreviewModalMessage('')
        setHexagonPreviewData(preview)
        setHexagonPreviewPage(preview.preview_images_pagination.page)
        setHexagonPreviewModalOpen(true)
      })
      .catch(error => {
        const detail =
          error?.response?.data?.detail ??
          'Preview failed. Check floor mapping and Hexagon credentials.'
        setHexagonPreviewModalTitle('Hexagon Preview Error')
        setHexagonPreviewModalMessage(detail)
        setHexagonPreviewData(null)
        setHexagonPreviewModalOpen(true)
      })
      .finally(() => {
        setHexagonPreviewLoading(false)
        setHexagonPreviewPageLoading(false)
      })
  }

  const navigatePreviewPage = (direction: 'prev' | 'next') => {
    if (!hexagonPreviewData) {
      return
    }
    const pagination = hexagonPreviewData.preview_images_pagination
    const targetPage =
      direction === 'prev' ? pagination.page - 1 : pagination.page + 1
    if (targetPage < 1 || targetPage > pagination.total_pages) {
      return
    }

    setHexagonPreviewLoading(true)
    setHexagonPreviewPageLoading(true)
    previewHexagonSync({
      maxPhotosPerDate: 6,
      previewPage: targetPage,
      imagesPerPage: 8,
      startDate: hexagonStartDate || undefined,
      endDate: hexagonEndDate || undefined,
      syncClosestVpMatch: hexagonSyncClosestVpMatch,
    })
      .then(preview => {
        setHexagonPreviewData(preview)
        setHexagonPreviewPage(targetPage)
      })
      .catch(error => {
        const detail =
          error?.response?.data?.detail ?? 'Unable to load next preview page.'
        setHexagonPreviewModalTitle('Hexagon Preview Error')
        setHexagonPreviewModalMessage(detail)
        setHexagonPreviewData(null)
      })
      .finally(() => {
        setHexagonPreviewLoading(false)
        setHexagonPreviewPageLoading(false)
      })
  }

  return (
    <div>
      {!dataLoaded && <LoadingIndicator />}
      {dataLoaded && (
        <Flex
          style={{
            gap: '16px',
            alignItems: 'stretch',
            flexWrap: 'wrap',
          }}
        >
          <IntegrationAuthCard
            logoSrc={procoreLogo}
            title="Procore"
            description="Configure Procore integration"
            authenticateLabel="Authenticate with Procore"
            isLinked={linkedWithProcore}
            loginUrl={procoreLoginUrl}
          />

          <IntegrationAuthCard
            logoSrc={formaLogo}
            title="Autodesk Forma"
            description="Configure Autodesk Forma integration"
            authenticateLabel="Authenticate with Autodesk Forma"
            isLinked={false}
            loginUrl={formaLoginUrl}
          />

          <IntegrationAuthCard
            logoSrc={oracleUnifierLogo}
            title="Oracle Primavera Unifier"
            description="Configure Oracle Primavera Unifier integration"
            authenticateLabel="Authenticate with Primavera Unifier"
            isLinked={false}
            loginUrl={unifierLoginUrl}
          />

          <IntegrationAuthCard
            logoSrc={oracleP6Logo}
            title="Oracle Primavera P6"
            description="Configure Oracle Primavera P6 integration"
            authenticateLabel="Authenticate with Primavera P6"
            isLinked={false}
            loginUrl={p6LoginUrl}
          />

          <IntegrationAuthCard
            logoSrc={droneDeployLogo}
            title="DroneDeploy"
            description="Configure DroneDeploy integration"
            authenticateLabel="Authenticate with DroneDeploy"
            isLinked={false}
            loginUrl="https://www.dronedeploy.com/app2/auth/signin"
          />

          <CardBase>
            <CardImage
              src={oracleP6Logo}
              style={{
                width: '112px',
                height: '112px',
                objectFit: 'contain',
                margin: '20px auto 0',
                display: 'block',
              }}
            />
            <CardTitle>
              <CardTitleInner>Schedule Import</CardTitleInner>
            </CardTitle>
            <CardDesc
              style={{
                marginTop: '5px',
                display: 'block',
              }}
            >
              <CardDescItem style={{ whiteSpace: 'normal', lineHeight: '20px' }}>
                Import P6 schedule file
              </CardDescItem>
              <CardDescItem
                style={{
                  marginTop: '20px',
                }}
              >
                <input
                  ref={scheduleFileInputRef}
                  type="file"
                  accept=".xer,text/plain"
                  style={{ display: 'none' }}
                  onChange={onScheduleFileSelected}
                />
                <AddProjectButton
                  text={scheduleUploadBusy ? 'Parsing…' : 'Upload Schedule'}
                  onClick={e => {
                    e.stopPropagation()
                    if (!scheduleUploadBusy) {
                      openScheduleFilePicker()
                    }
                  }}
                  buttonStyle={{
                    width: '100%',
                    opacity: scheduleUploadBusy ? 0.6 : 1,
                    pointerEvents: scheduleUploadBusy ? 'none' : 'auto',
                  }}
                />
              </CardDescItem>
            </CardDesc>
          </CardBase>

          {isSuperUser ? (
            <CardBase>
              <CardImage
                src={hexagonLogo}
                style={{
                  width: '112px',
                  height: '112px',
                  objectFit: 'contain',
                  margin: '20px auto 0',
                  display: 'block',
                }}
              />
              <CardTitle>
                <CardTitleInner>Hexagon / MultiVista</CardTitleInner>
              </CardTitle>
              <CardDesc
                style={{
                  marginTop: '5px',
                  display: 'block',
                }}
              >
                <CardDescItem
                  style={{
                    whiteSpace: 'normal',
                    lineHeight: '20px',
                  }}
                >
                  Sync latest images from Hexagon
                </CardDescItem>
                <CardDescItem
                  style={{
                    marginTop: '8px',
                    display: 'block',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      width: '100%',
                      height: '44px',
                      marginTop: '20px',
                    }}
                  >
                    <input
                      type="date"
                      value={hexagonStartDate}
                      onChange={e => setHexagonStartDate(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        flex: '1 1 0',
                        minWidth: 0,
                        width: 0,
                        height: '44px',
                        boxSizing: 'border-box',
                        padding: '0 8px',
                        fontSize: '13px',
                      }}
                    />
                    <input
                      type="date"
                      value={hexagonEndDate}
                      onChange={e => setHexagonEndDate(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        flex: '1 1 0',
                        minWidth: 0,
                        width: 0,
                        height: '44px',
                        boxSizing: 'border-box',
                        padding: '0 8px',
                        fontSize: '13px',
                      }}
                    />
                    <button
                      type="button"
                      title="Preview Sync"
                      aria-label="Preview Sync"
                      disabled={hexagonPreviewLoading}
                      onClick={e => {
                        e.stopPropagation()
                        if (hexagonPreviewLoading) {
                          return
                        }
                        setHexagonPreviewPage(1)
                        runHexagonPreview()
                      }}
                      style={{
                        flexShrink: 0,
                        width: '44px',
                        height: '44px',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        border: '1px solid #073c7a',
                        borderRadius: '2px',
                        background: '#ffffff',
                        color: '#073c7a',
                        cursor: hexagonPreviewLoading ? 'default' : 'pointer',
                        opacity: hexagonPreviewLoading ? 0.75 : 1,
                      }}
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                      >
                        <path
                          d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0 0 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 0 0 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"
                          fill="currentColor"
                        />
                      </svg>
                    </button>
                  </div>
                </CardDescItem>
              </CardDesc>
            </CardBase>
          ) : null}
        </Flex>
      )}
      <Modal
        isOpen={hexagonPreviewModalOpen}
        onRequestClose={() => setHexagonPreviewModalOpen(false)}
        contentLabel="Hexagon Preview Modal"
        style={{
          overlay: {
            position: 'fixed',
            zIndex: 1000,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
          },
          content: {
            position: 'absolute',
            top: '40px',
            left: '40px',
            right: '40px',
            bottom: '40px',
            border: '1px solid #ccc',
            background: '#fff',
            borderRadius: '4px',
            outline: 'none',
            padding: '20px',
            maxWidth: '900px',
            margin: 'auto',
            overflow: 'hidden',
          },
        }}
      >
        <h3
          style={{
            textAlign: 'center',
            margin: 0,
            fontSize: '28px',
            lineHeight: '34px',
            fontWeight: 700,
            color: '#0A2458',
          }}
        >
          {hexagonPreviewModalTitle}
        </h3>
        <div
          style={{
            marginTop: '8px',
            color: '#333',
            maxHeight: '65vh',
            overflowY: 'auto',
            paddingRight: '8px',
            whiteSpace: 'normal',
            lineHeight: '20px',
          }}
        >
          {!(
            hexagonPreviewLoading &&
            !hexagonPreviewData &&
            hexagonStatus !== 'queued' &&
            hexagonStatus !== 'running'
          ) && (
            <div style={{ textAlign: 'center' }}>
              {hexagonPreviewModalMessage}
            </div>
          )}
          {(hexagonPreviewLoading ||
            hexagonStatus === 'queued' ||
            hexagonStatus === 'running') && (
            <div
              style={{
                marginTop: hexagonPreviewData ? '10px' : '24px',
                marginBottom: '8px',
                minHeight: hexagonPreviewData ? undefined : '120px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                color: '#0A2458',
                fontSize: '13px',
              }}
            >
              <LoadingIndicator size={hexagonPreviewData ? undefined : 55} />
              {hexagonPreviewLoading && !hexagonPreviewData ? (
                <span style={{ textAlign: 'center', maxWidth: '320px' }}>
                  {hexagonPreviewModalMessage || 'Preparing sync preview…'}
                </span>
              ) : null}
            </div>
          )}
          {(hexagonStatus === 'queued' || hexagonStatus === 'running') &&
          hexagonJobId ? (
            <div
              style={{
                marginTop: '8px',
                fontSize: '12px',
                color: '#555',
                textAlign: 'center',
                lineHeight: '1.45',
              }}
            >
              Job ID: <code style={{ userSelect: 'all' }}>{hexagonJobId}</code>
            </div>
          ) : null}
          {hexagonPreviewData ? (
            <>
              <div
                style={{
                  marginTop: '12px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: '8px',
                }}
              >
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#0A2458',
                    }}
                  >
                    Filter dates
                  </div>
                  <div>
                    {hexagonPreviewData.start_date} to{' '}
                    {hexagonPreviewData.end_date}
                  </div>
                </div>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#0A2458',
                    }}
                  >
                    Would upload
                  </div>
                  <div>
                    {hexagonPreviewData.totals.would_upload}/
                    {hexagonPreviewData.totals.candidate_photos}
                  </div>
                </div>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#0A2458',
                    }}
                  >
                    Direct VP match
                  </div>
                  <div>{hexagonPreviewData.totals.would_upload_direct_vp}</div>
                </div>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#0A2458',
                    }}
                  >
                    Closest VP match
                  </div>
                  <div>{hexagonPreviewData.totals.would_upload_closest_vp}</div>
                </div>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#0A2458',
                    }}
                  >
                    Skip: no point
                  </div>
                  <div>{hexagonPreviewData.totals.would_skip_no_point}</div>
                </div>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#0A2458',
                    }}
                  >
                    Skip: missing URL
                  </div>
                  <div>
                    {hexagonPreviewData.totals.would_skip_missing_image_url}
                  </div>
                </div>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#0A2458',
                    }}
                  >
                    Skip: already synced
                  </div>
                  <div>
                    {hexagonPreviewData.totals.would_skip_already_synced ?? 0}
                  </div>
                </div>
                <div
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                    gridColumn: '1 / -1',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '14px',
                      color: '#0A2458',
                    }}
                  >
                    Grid miss, nearby DB viewpoint exists
                  </div>
                  <div>
                    {
                      hexagonPreviewData.totals
                        .skip_no_point_has_nearby_db_point
                    }
                  </div>
                </div>
              </div>
              <div
                style={{
                  marginTop: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  padding: '10px',
                  fontSize: '13px',
                  color: '#222',
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#0A2458',
                    marginBottom: '6px',
                  }}
                >
                  Viewpoint matching (DB grid)
                </div>
                {hexagonPreviewData.floors.map(f => (
                  <div
                    key={f.floor_code}
                    style={{
                      marginTop: '6px',
                      paddingTop: '6px',
                      borderTop: '1px solid #eee',
                      fontSize: '13px',
                      lineHeight: '1.45',
                    }}
                  >
                    <strong>{f.floor_code}</strong>: DB grid points{' '}
                    {f.db_grid_point_count} — {f.matched_viewpoint_photos}{' '}
                    matched viewpoint photos
                  </div>
                ))}
                {hexagonPreviewData.unmatched_viewpoint_rows.length > 0 ? (
                  <div style={{ marginTop: '12px' }}>
                    <div
                      style={{
                        fontWeight: 600,
                        marginBottom: '6px',
                        fontSize: '12px',
                      }}
                    >
                      Unmatched photos (
                      {hexagonPreviewData.unmatched_viewpoint_rows.length})
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '11px',
                          fontFamily: 'system-ui, sans-serif',
                        }}
                      >
                        <thead>
                          <tr
                            style={{ background: '#f5f5f5', textAlign: 'left' }}
                          >
                            <th
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                              }}
                            >
                              Floor
                            </th>
                            <th
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                              }}
                            >
                              Date
                            </th>
                            <th
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                              }}
                            >
                              Photo
                            </th>
                            <th
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                              }}
                            >
                              Grid (x,y)
                            </th>
                            <th
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                              }}
                            >
                              Closest VP match
                            </th>
                            <th
                              style={{
                                padding: '4px',
                                border: '1px solid #ddd',
                              }}
                            >
                              Has image
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {hexagonPreviewData.unmatched_viewpoint_rows.map(
                            (row, i) => (
                              <tr
                                key={`${row.floor_code}-${row.date}-${row.photo_number}-${i}`}
                              >
                                <td
                                  style={{
                                    padding: '4px',
                                    border: '1px solid #eee',
                                  }}
                                >
                                  {row.floor_code}
                                </td>
                                <td
                                  style={{
                                    padding: '4px',
                                    border: '1px solid #eee',
                                  }}
                                >
                                  {row.date}
                                </td>
                                <td
                                  style={{
                                    padding: '4px',
                                    border: '1px solid #eee',
                                  }}
                                >
                                  {row.photo_number || '—'}
                                </td>
                                <td
                                  style={{
                                    padding: '4px',
                                    border: '1px solid #eee',
                                    fontFamily: 'monospace',
                                  }}
                                >
                                  {row.grid_x},{row.grid_y}
                                </td>
                                <td
                                  style={{
                                    padding: '4px',
                                    border: '1px solid #eee',
                                    fontFamily: 'monospace',
                                    fontSize: '11px',
                                  }}
                                >
                                  {row.closest_vp_match ?? '—'}
                                </td>
                                <td
                                  style={{
                                    padding: '4px',
                                    border: '1px solid #eee',
                                  }}
                                >
                                  {row.has_image_url ? 'yes' : 'no'}
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      marginTop: '10px',
                      fontSize: '12px',
                      color: '#555',
                    }}
                  >
                    No unmatched photos in this date range for the current
                    matching mode.
                  </div>
                )}
              </div>
              {hexagonPreviewData.preview_images.length > 0 ? (
                <div style={{ marginTop: '14px' }}>
                  <div style={{ marginBottom: '8px' }}>
                    Preview images (page{' '}
                    {hexagonPreviewData.preview_images_pagination.page} of{' '}
                    {hexagonPreviewData.preview_images_pagination.total_pages ||
                      1}
                    ). Each thumbnail links to the matched Didge viewpoint when
                    a public app URL is configured.
                  </div>
                  {!hexagonPreviewPageLoading ? (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          'repeat(auto-fill, minmax(150px, 1fr))',
                        gap: '10px',
                      }}
                    >
                      {hexagonPreviewData.preview_images.map(
                        (item: HexagonSyncPreviewImageItem, idx) => {
                          const didgeHref = hexagonPreviewDidgePointUrl(
                            hexagonPreviewData.project_public_id,
                            item.floor_code,
                            item.matched_sub_viewpoint_id,
                          )
                          const img = (
                            <img
                              src={item.image_url}
                              alt="Hexagon preview"
                              style={{
                                width: '100%',
                                height: '120px',
                                objectFit: 'cover',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                display: 'block',
                                cursor: didgeHref ? 'pointer' : 'default',
                              }}
                            />
                          )
                          return (
                            <div key={`${item.image_url}-${idx}`}>
                              {didgeHref ? (
                                <a
                                  href={didgeHref}
                                  target="_blank"
                                  rel="noreferrer"
                                  title="Open matched viewpoint in Didge"
                                >
                                  {img}
                                </a>
                              ) : (
                                img
                              )}
                            </div>
                          )
                        },
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        minHeight: '180px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    >
                      <LoadingIndicator />
                    </div>
                  )}
                  <div
                    style={{
                      marginTop: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '8px',
                    }}
                  >
                    <AddProjectButton
                      text="<"
                      onClick={() => navigatePreviewPage('prev')}
                      buttonStyle={{
                        width: '80px',
                        opacity: hexagonPreviewData.preview_images_pagination
                          .has_prev
                          ? 1
                          : 0.5,
                        cursor: hexagonPreviewData.preview_images_pagination
                          .has_prev
                          ? 'pointer'
                          : 'default',
                      }}
                    />
                    <AddProjectButton
                      text=">"
                      onClick={() => navigatePreviewPage('next')}
                      buttonStyle={{
                        width: '80px',
                        opacity: hexagonPreviewData.preview_images_pagination
                          .has_next
                          ? 1
                          : 0.5,
                        cursor: hexagonPreviewData.preview_images_pagination
                          .has_next
                          ? 'pointer'
                          : 'default',
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </div>
        <div
          style={{
            marginTop: '16px',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              color: '#222',
              flex: '1 1 260px',
              maxWidth: '100%',
              lineHeight: '1.35',
            }}
          >
            <input
              type="checkbox"
              checked={hexagonSyncClosestVpMatch}
              onChange={e => {
                setHexagonSyncClosestVpMatch(e.target.checked)
              }}
              style={{ marginTop: '3px', flexShrink: 0 }}
            />
            <span>
              Also sync using the <strong>closest</strong> database grid
              viewpoint when the exact cell is missing (within the API search
              radius). Re-run Preview Sync to refresh numbers after changing
              this.
            </span>
          </label>
          <div
            style={{
              display: 'flex',
              gap: '8px',
              marginLeft: 'auto',
            }}
          >
            {hexagonStatus === 'success' && hexagonSyncReportSummary ? (
              <AddProjectButton
                text="View Sync Report"
                onClick={() => setHexagonSyncReportOpen(true)}
                buttonStyle={{ width: '180px', height: '44px' }}
              />
            ) : null}
            <AddProjectButton
              text={
                previewNeedsRefresh
                  ? hexagonPreviewLoading
                    ? 'Refreshing...'
                    : 'Refresh Preview'
                  : hexagonStatus === 'queued' || hexagonStatus === 'running'
                    ? 'Syncing...'
                    : 'Sync Images'
              }
              onClick={() => {
                if (previewNeedsRefresh) {
                  if (hexagonPreviewLoading) {
                    return
                  }
                  setHexagonPreviewPage(1)
                  runHexagonPreview()
                  return
                }
                if (hexagonStatus === 'queued' || hexagonStatus === 'running') {
                  return
                }
                startHexagonSync()
              }}
              buttonStyle={{
                width: '180px',
                height: '44px',
                cursor:
                  (hexagonStatus === 'queued' || hexagonStatus === 'running') &&
                  !previewNeedsRefresh
                    ? 'default'
                    : 'pointer',
                opacity:
                  ((hexagonStatus === 'queued' ||
                    hexagonStatus === 'running') &&
                    !previewNeedsRefresh) ||
                  (previewNeedsRefresh && hexagonPreviewLoading)
                    ? 0.75
                    : 1,
              }}
            />
            <AddProjectButton
              text="Close"
              onClick={() => setHexagonPreviewModalOpen(false)}
              buttonStyle={{ width: '140px', height: '44px' }}
            />
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={hexagonSyncReportOpen}
        onRequestClose={() => setHexagonSyncReportOpen(false)}
        contentLabel="Hexagon sync report"
        style={{
          overlay: {
            position: 'fixed',
            zIndex: 1010,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
          content: {
            position: 'absolute',
            top: '48px',
            left: '40px',
            right: '40px',
            bottom: '48px',
            border: '1px solid #ccc',
            background: '#fff',
            borderRadius: '4px',
            outline: 'none',
            padding: '20px',
            maxWidth: '720px',
            margin: 'auto',
            overflow: 'auto',
            maxHeight: 'min(880px, 90vh)',
          },
        }}
      >
        <h3
          style={{
            textAlign: 'center',
            margin: 0,
            fontSize: '24px',
            lineHeight: '30px',
            fontWeight: 700,
            color: '#0A2458',
          }}
        >
          Hexagon sync report
        </h3>
        {hexagonSyncReportSummary ? (
          <div
            style={{
              marginTop: '14px',
              color: '#333',
              fontSize: '14px',
              lineHeight: '1.45',
            }}
          >
            <div
              style={{
                textAlign: 'center',
                marginBottom: '12px',
                color: '#555',
              }}
            >
              <strong>{hexagonSyncReportSummary.project_name ?? '—'}</strong>
              {hexagonSyncReportSummary.project_public_id
                ? ` · ${hexagonSyncReportSummary.project_public_id}`
                : ''}
            </div>
            <div
              style={{
                fontSize: '13px',
                color: '#444',
                marginBottom: '14px',
                textAlign: 'center',
              }}
            >
              Range: {hexagonSyncReportSummary.start_date ?? '—'} to{' '}
              {hexagonSyncReportSummary.end_date ?? '—'}
              {hexagonSyncReportSummary.last_synced_on ? (
                <>
                  <br />
                  Finished: {hexagonSyncReportSummary.last_synced_on}
                </>
              ) : null}
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '8px',
              }}
            >
              {[
                [
                  'New images uploaded',
                  num(hexagonSyncReportSummary.uploaded_images),
                ],
                [
                  'Direct VP match',
                  num(hexagonSyncReportSummary.uploaded_via_direct_vp),
                ],
                [
                  'Closest VP match',
                  num(hexagonSyncReportSummary.uploaded_via_closest_vp),
                ],
                [
                  'Skipped (no DB viewpoint)',
                  num(hexagonSyncReportSummary.skipped_images_no_point),
                ],
                [
                  'Skipped (already synced)',
                  num(hexagonSyncReportSummary.skipped_already_synced),
                ],
                [
                  'Dates with no pending uploads',
                  num(hexagonSyncReportSummary.dates_skipped_no_pending_work),
                ],
              ].map(([label, value]) => (
                <div
                  key={String(label)}
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '8px',
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      fontSize: '13px',
                      color: '#0A2458',
                    }}
                  >
                    {label}
                  </div>
                  <div>{value}</div>
                </div>
              ))}
            </div>
            {hexagonSyncReportSummary.db_viewpoint_grid_point_count_by_floor &&
            Object.keys(
              hexagonSyncReportSummary.db_viewpoint_grid_point_count_by_floor,
            ).length > 0 ? (
              <div style={{ marginTop: '14px' }}>
                <div
                  style={{
                    fontWeight: 700,
                    color: '#0A2458',
                    marginBottom: '6px',
                  }}
                >
                  DB grid points by floor
                </div>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {Object.entries(
                    hexagonSyncReportSummary.db_viewpoint_grid_point_count_by_floor,
                  ).map(([code, count]) => (
                    <li key={code}>
                      <strong>{code}</strong>: {count}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {hexagonSyncReportSummary.floor_upload_counts_by_floor &&
            Object.keys(hexagonSyncReportSummary.floor_upload_counts_by_floor)
              .length > 0 ? (
              <div style={{ marginTop: '14px' }}>
                <div
                  style={{
                    fontWeight: 700,
                    color: '#0A2458',
                    marginBottom: '6px',
                  }}
                >
                  Uploaded by floor (uploaded/attempted)
                </div>
                <div
                  style={{
                    maxHeight: '220px',
                    overflowY: 'auto',
                    border: '1px solid #eee',
                  }}
                >
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '13px',
                    }}
                  >
                    <thead>
                      <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                        <th
                          style={{
                            padding: '6px',
                            borderBottom: '1px solid #ddd',
                          }}
                        >
                          Floor
                        </th>
                        <th
                          style={{
                            padding: '6px',
                            borderBottom: '1px solid #ddd',
                          }}
                        >
                          Uploaded
                        </th>
                        <th
                          style={{
                            padding: '6px',
                            borderBottom: '1px solid #ddd',
                          }}
                        >
                          Attempted
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(
                        hexagonSyncReportSummary.floor_upload_counts_by_floor,
                      ).map(([code, counts]) => (
                        <tr key={code}>
                          <td
                            style={{
                              padding: '6px',
                              borderBottom: '1px solid #f0f0f0',
                            }}
                          >
                            {code}
                          </td>
                          <td
                            style={{
                              padding: '6px',
                              borderBottom: '1px solid #f0f0f0',
                            }}
                          >
                            {num(counts?.uploaded)}
                          </td>
                          <td
                            style={{
                              padding: '6px',
                              borderBottom: '1px solid #f0f0f0',
                            }}
                          >
                            {num(counts?.attempted)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
            {hexagonSyncReportSummary.synced_dates &&
            hexagonSyncReportSummary.synced_dates.length > 0 ? (
              <div style={{ marginTop: '14px' }}>
                <div
                  style={{
                    fontWeight: 700,
                    color: '#0A2458',
                    marginBottom: '6px',
                  }}
                >
                  Floors/dates with new uploads (
                  {hexagonSyncReportSummary.synced_dates.length})
                </div>
                <div
                  style={{
                    maxHeight: '220px',
                    overflowY: 'auto',
                    border: '1px solid #eee',
                  }}
                >
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '13px',
                    }}
                  >
                    <thead>
                      <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                        <th
                          style={{
                            padding: '6px',
                            borderBottom: '1px solid #ddd',
                          }}
                        >
                          Floor
                        </th>
                        <th
                          style={{
                            padding: '6px',
                            borderBottom: '1px solid #ddd',
                          }}
                        >
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {hexagonSyncReportSummary.synced_dates.map((row, i) => (
                        <tr key={`${row.floor_code}-${row.date}-${i}`}>
                          <td
                            style={{
                              padding: '6px',
                              borderBottom: '1px solid #f0f0f0',
                            }}
                          >
                            {row.floor_code}
                          </td>
                          <td
                            style={{
                              padding: '6px',
                              borderBottom: '1px solid #f0f0f0',
                            }}
                          >
                            {row.date}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div
                style={{ marginTop: '14px', color: '#666', fontSize: '13px' }}
              >
                No floor/date rows recorded with new uploads in this run.
              </div>
            )}
          </div>
        ) : (
          <p style={{ textAlign: 'center', marginTop: '16px' }}>
            No summary was returned for this job.
          </p>
        )}
        <div
          style={{
            marginTop: '20px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <AddProjectButton
            text="Close"
            onClick={() => setHexagonSyncReportOpen(false)}
            buttonStyle={{ width: '160px', height: '44px' }}
          />
        </div>
      </Modal>
    </div>
  )
}
